import React,  { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import SearchConds from './SearchConds';
import { useApi } from '../../hooks/useApi';
import { Donor, ApiDonor,  } from '../../models/Donor';
import Spinner from '../../components/Spinner';
import DonorCard from '../../components/DonorCard';
import IconGuide from '../../components/IconGuide';
import FloatActionBar from './FloatActionBar';
import { useAuthUserContext } from '../../providers';
import { UserType } from '../../types';

const STORAGE_KEY = 'search_result_storage';
const SEARCH_PARAMS_KEY = 'search_result_search_params';
const SCROLL_POSITION_KEY = 'search_result_scroll_position';

const ResultDonors = () => {
  const [ apiDonors, setApiDonors ] = useState<ApiDonor[]>([]);
  const limit = 25;
  const [ offset, setOffset ] = useState(0);
  const [ hasMore, setHasMore ] = useState(true);
  const { post, loading } = useApi();
  const location = useLocation();
  const lastDonorElementRef = useRef(null);
  const isRestoredFromCacheRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const authUser = useAuthUserContext().user;
  const isDonee = authUser?.userType === UserType.Donee;

  useEffect(() => {
    const cachedData = sessionStorage.getItem(STORAGE_KEY);
    const cachedSearch = sessionStorage.getItem(SEARCH_PARAMS_KEY);

    if (cachedData && cachedSearch === location.search) {
      isRestoredFromCacheRef.current = true;

      const { donors, offset: cachedOffset, hasMore: cachedHasMore } = JSON.parse(cachedData);
      setApiDonors(donors);
      setOffset(cachedOffset);
      setHasMore(cachedHasMore);

      // スクロール位置を復元
      const scrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
      if (scrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(scrollPosition));
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRestoredFromCacheRef.current) {
      isRestoredFromCacheRef.current = false;
      return;
    }


    if (offset < apiDonors.length) return;

    const params = new URLSearchParams(location.search.slice(1));
    params.append('limit', String(limit));
    params.append('offset', String(offset));

    post({
      endpoint: `/api/v1/donors/search`,
      skipAuth: true,
      params,
      onSuccess: (res) => {
        if (offset === 0) {
          setApiDonors(res?.data || []);
        } else {
          setApiDonors(prevDonors => [...prevDonors, ...(res?.data || [])]);
        }
        setHasMore((res?.data || []).length === limit);
        console.log(apiDonors.length + res?.data.length);
      },
      onError: (err) => {
        console.error(err.message);
      },
    });
  }, [ post, location.search, limit, offset, apiDonors.length ]);

  useEffect(() => {
    if (loading || !hasMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setOffset(prevOffset => prevOffset + limit);
      }
    }, { threshold: 1.0 }); // Trigger when 100% of the target is visible

    const lastDonorElement = lastDonorElementRef.current;
    if (lastDonorElement) {
      observer.observe(lastDonorElement);
    }

    return () => {
      if (lastDonorElement) {
        observer.unobserve(lastDonorElement);
      }
    };
  }, [ loading, hasMore, limit, apiDonors ]);

  // データが更新されたらsessionStorageに保存
  useEffect(() => {
    if (apiDonors.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        donors: apiDonors,
        offset,
        hasMore
      }));
      sessionStorage.setItem(SEARCH_PARAMS_KEY, location.search);
    }
  }, [ apiDonors, offset, hasMore, location.search ]);

  const clearResults = () => {
    setApiDonors([]);
    setOffset(0);
    setHasMore(true);
    // キャッシュもクリア
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SEARCH_PARAMS_KEY);
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
  }

  const handleDonorClick = () => {
    // ドナー詳細に遷移する前にスクロール位置を保存
    sessionStorage.setItem(SCROLL_POSITION_KEY, String(window.scrollY));
  }

  const donorItems = apiDonors.map((apiDonor, index) => {
    const donor = new Donor(apiDonor);
    const isLastDonor = index === apiDonors.length - 1;

    return (
      <li key={ donor.userNo } ref={ isLastDonor ? lastDonorElementRef : null }>
        <DonorCard donor={ donor } onClick={ handleDonorClick } />
      </li>
    )
  });

  return (
      <section className="search_donor" style={{ position: 'relative' }}>
        <div ref={ sentinelRef } aria-hidden="true" style={{ position: 'absolute', top: '560px', left: 0, width: '1px', height: '1px', pointerEvents: 'none' }} />
        <h2 className="main_title">
          <span>検索結果</span>
        </h2>
        <div className="wrap">
          <SearchConds clearResults={ clearResults } />
          <div className="result-area">
            <div className="icon-guide-wrap">
              <IconGuide />
            </div>
            <ul>
              { loading && offset === 0 ? <Spinner centered /> : donorItems }
              { loading && offset > 0 && <Spinner centered /> }
              { !loading && donorItems.length === 0 && <span>該当するドナーが見つかりませんでした。</span> }
            </ul>
          </div>
        </div>
        <FloatActionBar sentinelRef={ sentinelRef } isDonee={ isDonee } />
      </section>
  );
}

export default ResultDonors;
